/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import * as z from "zod";
import type { ToZod } from "../.kubb/ToZod.ts";
import type { CourseFinancialEditorViewModel } from "../types/CourseFinancialEditorViewModel.ts";
import { courseFinancialCostCenterEditorViewModelSchema } from "./courseFinancialCostCenterEditorViewModelSchema.ts";
import { courseFinancialPaymentMethodEditorViewModelSchema } from "./courseFinancialPaymentMethodEditorViewModelSchema.ts";
import { ePaymentReceiptTypeSchema } from "./ePaymentReceiptTypeSchema.ts";

export const courseFinancialEditorViewModelSchema = z.object({
  description: z
    .string()
    .describe(
      "Descrição do plano. Obrigatória, máximo de 255 caracteres. É replicada na descrição do idioma padrão.",
    )
    .nullish(),
  bankId: z.optional(
    z
      .number()
      .int()
      .describe(
        "ID do banco do plano. Obrigatório. As formas de pagamento informadas precisam estar habilitadas neste banco.",
      ),
  ),
  startDate: z.optional(
    z.string().datetime().describe("Início da vigência do plano. Obrigatório."),
  ),
  endDate: z
    .string()
    .datetime()
    .describe(
      "Fim da vigência do plano. Não pode ser anterior ao início. É normalizado para 23:59:59 do dia informado. Opcional.",
    )
    .nullish(),
  active: z.optional(
    z
      .boolean()
      .describe(
        "Situação do plano. Ao inativar, a data de inativação é registrada.",
      ),
  ),
  totalValue: z
    .number()
    .describe(
      "Valor total do plano. Entre 0 e 99.999.999,99. Bloqueado quando há matrículas.",
    )
    .nullish(),
  valueRegistration: z
    .number()
    .describe(
      "Taxa de inscrição. Entre 0 e 99.999.999,99. Obrigatória e maior que zero quando o curso exige aprovação de documentação. Bloqueada quando há matrículas.",
    )
    .nullish(),
  interest: z
    .number()
    .describe(
      "Juros ao mês, em percentual. Entre 0 e 100. Bloqueado quando há matrículas.",
    )
    .nullish(),
  fine: z
    .number()
    .describe(
      "Multa por atraso, em percentual. Entre 0 e 100. Bloqueada quando há matrículas.",
    )
    .nullish(),
  discountPunctualPercentage: z
    .number()
    .describe(
      "Desconto de pontualidade, em percentual. Entre 0 e 100. Alterável mesmo com matrículas.",
    )
    .nullish(),
  inCashDiscount: z.optional(
    z
      .number()
      .describe(
        "Desconto à vista, em percentual. Entre 0 e 100. Bloqueado quando há matrículas.",
      ),
  ),
  showInCashDiscount: z.optional(
    z.boolean().describe("Define se o desconto à vista é exibido ao aluno."),
  ),
  extendExpiration: z.optional(
    z
      .boolean()
      .describe(
        "Define se o vencimento é prorrogado quando cai em dia não útil. Bloqueado quando há matrículas.",
      ),
  ),
  disableUser: z.optional(
    z
      .boolean()
      .describe(
        "Define se o aluno inadimplente é desabilitado. Quando falso, os dias e parcelas para desabilitar são descartados.",
      ),
  ),
  daysToDisable: z
    .number()
    .int()
    .describe(
      "Dias de atraso para desabilitar o aluno. Entre 0 e 999. Obrigatório quando DisableUser é verdadeiro.",
    )
    .nullish(),
  daysToDisableRenegotiation: z
    .number()
    .int()
    .describe(
      "Dias de atraso para desabilitar o aluno em renegociação. Entre 0 e 999. Opcional.",
    )
    .nullish(),
  parcelsToDisable: z
    .number()
    .int()
    .describe(
      "Parcelas em atraso para desabilitar o aluno. Entre 0 e 999. Opcional.",
    )
    .nullish(),
  parcelsToDisableRenegotiation: z
    .number()
    .int()
    .describe(
      "Parcelas em atraso para desabilitar o aluno em renegociação. Entre 0 e 999. Opcional.",
    )
    .nullish(),
  instruction1: z
    .string()
    .describe(
      "Primeira instrução do boleto. Máximo de 100 caracteres. Quando vazia, assume a instrução padrão do banco.",
    )
    .nullish(),
  instruction2: z
    .string()
    .describe(
      "Segunda instrução do boleto. Máximo de 100 caracteres. Quando vazia, assume a instrução padrão do banco.",
    )
    .nullish(),
  instruction3: z
    .string()
    .describe(
      "Terceira instrução do boleto. Máximo de 100 caracteres. Quando vazia, assume a instrução padrão do banco.",
    )
    .nullish(),
  message: z
    .string()
    .describe(
      "Mensagem livre impressa no boleto. Máximo de 200 caracteres. Opcional.",
    )
    .nullish(),
  trailOnly: z.optional(
    z
      .boolean()
      .describe(
        "Define que o plano só pode ser usado em matrícula por trilha. Campo oculto na tela legada e exposto aqui.",
      ),
  ),
  dateStartPayment: z
    .string()
    .datetime()
    .describe(
      "Data de início dos pagamentos. Obrigatória quando ForceFirstInstallment é verdadeiro.",
    )
    .nullish(),
  preventFuturePayment: z.optional(
    z
      .boolean()
      .describe("Define se pagamentos com vencimento futuro são bloqueados."),
  ),
  forceFirstInstallment: z.optional(
    z
      .boolean()
      .describe(
        "Define se a data de vencimento da primeira parcela é forçada. Não pode ser combinado com DaysToFirstParcelMaturity — no legado essa exclusividade existia apenas no JavaScript da tela.",
      ),
  ),
  daysToFirstParcelMaturity: z
    .number()
    .int()
    .describe(
      "Dias para o vencimento da primeira parcela. Entre 0 e 999. Deve ser nulo quando ForceFirstInstallment é verdadeiro.",
    )
    .nullish(),
  daysToRegistrationMaturity: z
    .number()
    .int()
    .describe(
      "Dias para o vencimento da taxa de inscrição. Entre 0 e 999. Opcional.",
    )
    .nullish(),
  enableDefaultingBlock: z.optional(
    z
      .boolean()
      .describe(
        "Define se o bloqueio de alunos inadimplentes está ativo. Quando falso, o período de verificação e os dias para bloqueio são descartados.",
      ),
  ),
  periodDefaultingCheck: z
    .number()
    .int()
    .describe(
      "Período de verificação de inadimplência, em meses. Entre 1 e 24. Obrigatório quando EnableDefaultingBlock é verdadeiro.",
    )
    .nullish(),
  daysToBlock: z
    .number()
    .int()
    .describe(
      "Dias de atraso para bloquear o aluno. Entre 0 e 360. Obrigatório quando EnableDefaultingBlock é verdadeiro.",
    )
    .nullish(),
  noPunctualityDiscountAfterExpiration: z.optional(
    z
      .boolean()
      .describe(
        "Define que o desconto de pontualidade não vale após o vencimento.",
      ),
  ),
  discountAutomatic: z.optional(
    z
      .boolean()
      .describe(
        "Define se o desconto automático do aluno está ativo. Quando falso, o percentual e os acúmulos são descartados. Campo oculto na tela legada e exposto aqui.",
      ),
  ),
  discountAutomaticPercentage: z
    .number()
    .describe("Percentual do desconto automático. Entre 0 e 100.")
    .nullish(),
  discountAutomaticAccumulateCoupon: z.optional(
    z.boolean().describe("Define se o desconto automático acumula com cupom."),
  ),
  discountAutomaticAccumulateInCash: z.optional(
    z
      .boolean()
      .describe(
        "Define se o desconto automático acumula com o desconto à vista.",
      ),
  ),
  internationalPlan: z.optional(
    z.boolean().describe("Define se é um plano internacional."),
  ),
  showRealToDollarConversion: z.optional(
    z
      .boolean()
      .describe("Define se a conversão de real para dólar é exibida ao aluno."),
  ),
  indicatorProgramDiscountValue: z
    .number()
    .describe(
      "Valor do desconto do programa aluno indica aluno. Entre 0 e 99.999.999,99. Opcional.",
    )
    .nullish(),
  paymentReceiptType: z.optional(
    z
      .lazy(() => ePaymentReceiptTypeSchema)
      .describe("1 = Invoice | 2 = Receipt | "),
  ),
  registrationCodeFealq: z
    .string()
    .describe(
      "Código do projeto de inscrição na fundação. Máximo de 30 caracteres. Opcional.",
    )
    .nullish(),
  paymentCodeFealq: z
    .string()
    .describe(
      "Código do projeto de mensalidade na fundação. Máximo de 30 caracteres. Opcional.",
    )
    .nullish(),
  classificationAccount: z
    .string()
    .describe(
      "GUID da classificação contábil no PecegePay. Descartado quando o banco não tem PecegePay habilitado. Opcional.",
    )
    .nullish(),
  paymentAccount: z
    .string()
    .describe(
      "GUID da conta de pagamento no PecegePay. Descartado quando o banco não tem PecegePay habilitado. Opcional.",
    )
    .nullish(),
  classificationUSP: z
    .string()
    .describe(
      "GUID da classificação USP. Descartado quando o banco não tem PecegePay habilitado. Opcional.",
    )
    .nullish(),
  classificationEnrollmentUSP: z
    .string()
    .describe(
      "GUID da classificação USP de inscrição. Descartado quando o banco não tem PecegePay habilitado. Opcional.",
    )
    .nullish(),
  paymentMethods: z
    .array(z.lazy(() => courseFinancialPaymentMethodEditorViewModelSchema))
    .describe(
      "Formas de pagamento do plano. Ao menos uma é obrigatória e não pode haver repetição.",
    )
    .nullish(),
  costCenters: z
    .array(z.lazy(() => courseFinancialCostCenterEditorViewModelSchema))
    .describe(
      "Rateio de centros de custo. Quando informado, os percentuais precisam somar exatamente 100 e não pode haver centro repetido.",
    )
    .nullish(),
  benefitsContemplated: z
    .array(z.number().int())
    .describe(
      "IDs dos benefícios contemplados. Precisam existir e estar ativos, e não podem repetir nem aparecer também como não contemplados.",
    )
    .nullish(),
  benefitsNotContemplated: z
    .array(z.number().int())
    .describe(
      "IDs dos benefícios não contemplados. Precisam existir e estar ativos, e não podem repetir nem aparecer também como contemplados.",
    )
    .nullish(),
}) as unknown as ToZod<CourseFinancialEditorViewModel>;
